<template>
	<div class="central-processamentos">
		<div class="central-processamentos__header">
			<label class="title_planning">{{
				ehCentralDeVinculos ? $t('vinculosPrognose') : $t('processamentosPrognose')
			}}</label>
			<Button
				:label="$t('back')"
				color="trv-primary"
				outlined
				semantic-class="tiny"
				width-auto
				@click="$router.push(ehCentralDeVinculos ? '/vincular' : '/prognose')"
			/>
		</div>

		<Card class="central-processamentos__card">
			<div
				v-if="carregando && !jobs.length"
				class="central-processamentos__vazio"
			>
				<Spinner />
			</div>
			<div
				v-else-if="!jobs.length"
				class="central-processamentos__vazio"
			>
				{{ $t('semProcessamentos') }}
			</div>
			<table
				v-else
				class="central-processamentos__tabela"
			>
				<thead>
					<tr>
						<th>{{ $t('description') }}</th>
						<th>{{ $t('status') }}</th>
						<th>{{ $t('progresso') }}</th>
						<th>{{ $t('criadoEm') }}</th>
						<th />
					</tr>
				</thead>
				<tbody>
					<tr
						v-for="job in jobs"
						:key="job.jobId"
					>
						<td>{{ job.descricao || '—' }}</td>
						<td>
							<span
								class="central-processamentos__status"
								:class="`central-processamentos__status--${job.status.toLowerCase()}`"
							>
								{{ $t(`jobStatus${job.status}`) }}
							</span>
						</td>
						<td class="central-processamentos__progresso">
							<template v-if="job.status === 'Processando'">
								<span class="central-processamentos__etapa">
									{{ job.etapa || $t('aguardandoInicioProcessamento') }}
									<template v-if="job.renderTotal > 0">
										— {{ job.renderFeitos }}/{{ job.renderTotal }}
									</template>
								</span>
								<div
									v-if="job.renderTotal > 0"
									class="central-processamentos__barra"
								>
									<div
										class="central-processamentos__barra-preenchida"
										:style="{ width: `${(job.renderFeitos / job.renderTotal) * 100}%` }"
									/>
								</div>
							</template>
							<span
								v-else-if="job.status === 'Falhou'"
								class="central-processamentos__erro"
								:title="job.erro"
							>
								{{ job.erro || '—' }}
							</span>
							<span v-else>—</span>
						</td>
						<td>{{ formataData(job.dataCadastro) }}</td>
						<td>
							<!-- Jobs de VÍNCULO não têm tela para reabrir (o resultado vira
							     registro aplicado): o botão abre um modal com o LOG do
							     processamento (etapas, datas, erro completo, resumo). -->
							<Button
								v-if="job.tipo === 'Vinculo'"
								:label="$t('detalhes')"
								color="trv-secondary"
								semantic-class="tiny"
								width-auto
								@click="abrirDetalhes(job)"
							/>
							<Button
								v-else-if="job.status !== 'Falhou'"
								:label="
									job.status === 'Concluido'
										? $t('abrirProcessamento')
										: $t('acompanharProcessamento')
								"
								color="trv-secondary"
								semantic-class="tiny"
								width-auto
								:disabled="abrindo === job.jobId"
								@click="abrir(job)"
							/>
						</td>
					</tr>
				</tbody>
			</table>
		</Card>

		<!-- Modal de detalhes (log) de um job de vínculo -->
		<div
			v-if="jobDetalhes"
			class="central-processamentos__modal-overlay"
			@click.self="jobDetalhes = null"
		>
			<Card class="central-processamentos__modal">
				<div class="central-processamentos__modal-header">
					<label class="title_planning">{{ $t('detalhesProcessamento') }}</label>
					<Button
						:label="$t('close')"
						color="trv-primary"
						outlined
						semantic-class="tiny"
						width-auto
						@click="jobDetalhes = null"
					/>
				</div>

				<dl class="central-processamentos__modal-lista">
					<dt>{{ $t('description') }}</dt>
					<dd>{{ jobDetalhes.descricao || '—' }}</dd>

					<dt>{{ $t('status') }}</dt>
					<dd>
						<span
							class="central-processamentos__status"
							:class="`central-processamentos__status--${jobDetalhes.status.toLowerCase()}`"
						>
							{{ $t(`jobStatus${jobDetalhes.status}`) }}
						</span>
					</dd>

					<dt>{{ $t('etapaAtual') }}</dt>
					<dd>{{ jobDetalhes.etapa || '—' }}</dd>

					<dt>{{ $t('criadoEm') }}</dt>
					<dd>{{ formataData(jobDetalhes.dataCadastro) }}</dd>

					<dt>{{ $t('atualizadoEm') }}</dt>
					<dd>{{ formataData(jobDetalhes.dataAtualizacao) }}</dd>

					<template v-if="jobDetalhes.resumo">
						<dt>{{ $t('plantiosVinculados') }}</dt>
						<dd>{{ jobDetalhes.resumo.plantios }}</dd>

						<dt>{{ $t('estratosAfetados') }}</dt>
						<dd>{{ jobDetalhes.resumo.grupos }}</dd>

						<dt>{{ $t('projecoesGeradas') }}</dt>
						<dd>{{ jobDetalhes.resumo.projecoes }}</dd>
					</template>
				</dl>

				<div
					v-if="jobDetalhes.erro"
					class="central-processamentos__modal-erro"
				>
					{{ jobDetalhes.erro }}
				</div>
			</Card>
		</div>
	</div>
</template>

<script>
import { mapMutations } from 'vuex'
import Card from '../../../components/Card/Card'
import Button from '../../../components/Button/Button'
import Spinner from '../../../components/Spinner/Spinner'
import {
	listarProcessamentosPrognose,
	contextoProcessamentoPrognose,
	statusProcessamentoPrognose,
} from '../../../services/requests/PrognoseRequests'

// Central de processamentos da prognose: lista os jobs assíncronos (com progresso
// ao vivo) e permite reabrir/acompanhar um job — inclusive depois de fechar a tela
// de planejamento. Os jobs são limpos do banco pela rotina de retenção (24h).
export default {
	data() {
		return {
			jobs: [],
			carregando: false,
			abrindo: null,
			// Job de vínculo aberto no modal de detalhes (log): metadados da linha +
			// resumo do resultado (plantios/grupos/projeções) buscado sob demanda.
			jobDetalhes: null,
			timer: null,
			destruido: false,
			// Falhas consecutivas do refresh: aplica backoff para não martelar um
			// servidor que está sofrendo (503/504) ou reciclando.
			falhasRefresh: 0,
		}
	},

	computed: {
		/**
		 * A central é compartilhada entre os dois fluxos e filtra por tipo de job:
		 * ?tipo=Vinculo (vinda da tela de Vincular) lista só vínculos; o default é
		 * só os processamentos de prognose (vinda da tela de Prognose).
		 */
		ehCentralDeVinculos() {
			return this.$route.query.tipo === 'Vinculo'
		},

		tipoJobs() {
			return this.ehCentralDeVinculos ? 'Vinculo' : 'Processamento'
		},
	},

	created() {
		this.tick()
	},

	beforeDestroy() {
		// destruido: o tick pode estar no meio de um await (buscar) quando a tela é
		// fechada; sem esta flag ele reagendaria um setTimeout órfão (refresh-zumbi
		// batendo no servidor após sair da central).
		this.destruido = true
		clearTimeout(this.timer)
	},

	methods: {
		...mapMutations('moduleMessages', ['mutationShowMessage']),

		// Loop de refresh auto-agendado (em vez de setInterval): 5s em condições
		// normais, backoff exponencial até 60s sob falha, e pausa enquanto a aba
		// está em background (volta a buscar quando o usuário retorna).
		async tick() {
			if (this.destruido) return
			if (document.hidden) {
				this.timer = setTimeout(this.tick, 5000)
				return
			}
			await this.buscar()
			if (this.destruido) return // tela fechada durante o await: não reagenda
			const atraso = this.falhasRefresh > 0 ? Math.min(5000 * 2 ** this.falhasRefresh, 60000) : 5000
			this.timer = setTimeout(this.tick, atraso)
		},

		async buscar() {
			try {
				this.carregando = true
				const res = await listarProcessamentosPrognose(20, this.tipoJobs)
				// BaseResponse devolve o payload CRU (sem envelope { result }); os
				// fallbacks cobrem camadas que envelopam (gateway).
				const payload = res.data
				this.jobs = Array.isArray(payload) ? payload : (payload?.result ?? payload?.data ?? [])
				this.falhasRefresh = 0
			} catch (error) {
				// Silencioso no refresh periódico; a tela mantém a última lista.
				this.falhasRefresh++
				console.warn('Falha ao listar processamentos:', error)
			} finally {
				this.carregando = false
			}
		},

		// Reabre um job: restaura o snapshot da tela (item/filtros) no localStorage —
		// mesmo caminho do goToPlanejamento — e navega com ?jobId para a tela retomar
		// o polling (ou carregar o resultado, se já concluiu).
		async abrir(job) {
			this.abrindo = job.jobId
			try {
				const res = await contextoProcessamentoPrognose(job.jobId)
				const requestJson =
					res.data?.result?.request ?? res.data?.data?.request ?? res.data?.request
				const request = requestJson ? JSON.parse(requestJson) : null
				// O back serializa o request em PascalCase; o snapshot é uma string
				// JSON pré-serializada pelo front (conteúdo camelCase).
				const contextoJson = request?.ContextoTelaJson ?? request?.contextoTelaJson
				const contexto = contextoJson ? JSON.parse(contextoJson) : null

				if (!contexto?.item) {
					this.mutationShowMessage({
						messageTitle: this.$t('erro'),
						messageText: this.$t('semContextoProcessamento'),
						messageIcon: 'far fa-times-circle',
						messageType: 'error',
					})
					return
				}

				localStorage.setItem('isEdit', 'false')
				localStorage.setItem('item-planejamento-prognose', JSON.stringify(contexto.item))
				localStorage.setItem(
					'contexto-processamento-prognose',
					JSON.stringify({
						filtros: contexto.filtros,
						filtroConfig: contexto.filtroConfig,
						filtroAplicado: contexto.filtroAplicado,
					}),
				)
				this.$router.push(`/prognose/planejamento/${job.jobId}?jobId=${job.jobId}`)
			} catch (error) {
				console.error('Erro ao abrir processamento:', error)
				this.mutationShowMessage({
					messageTitle: this.$t('erro'),
					messageText: this.$t('semContextoProcessamento'),
					messageIcon: 'far fa-times-circle',
					messageType: 'error',
				})
			} finally {
				this.abrindo = null
			}
		},

		formataData(data) {
			return data ? new Date(data).toLocaleString() : '—'
		},

		/**
		 * Modal de detalhes de um job de VÍNCULO: mostra a linha imediatamente e
		 * completa com o resumo do resultado (plantios/grupos/projeções) e o erro
		 * completo buscados do status do job.
		 */
		async abrirDetalhes(job) {
			this.jobDetalhes = { ...job, resumo: null }
			try {
				const res = await statusProcessamentoPrognose(job.jobId)
				const detalhe = res.data?.result ?? res.data?.data ?? res.data
				if (!detalhe || this.jobDetalhes?.jobId !== job.jobId) return

				this.jobDetalhes = {
					...this.jobDetalhes,
					etapa: detalhe.etapa ?? this.jobDetalhes.etapa,
					erro: detalhe.erro ?? this.jobDetalhes.erro,
					resumo: detalhe.resultado ? JSON.parse(detalhe.resultado) : null,
				}
			} catch (error) {
				// Mantém o modal com os dados da linha; o refresh da lista segue vivo.
				console.warn('Falha ao buscar detalhes do job:', error)
			}
		},
	},

	components: {
		Card,
		Button,
		Spinner,
	},
}
</script>

<style scoped>
.central-processamentos {
	display: flex;
	flex-direction: column;
	gap: 12px;
	padding: 16px;
	height: 100%;
}

.central-processamentos__header {
	display: flex;
	align-items: center;
	justify-content: space-between;
}

.central-processamentos__card {
	padding: 12px;
	overflow: auto;
}

.central-processamentos__vazio {
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 160px;
	color: #888;
}

.central-processamentos__tabela {
	width: 100%;
	border-collapse: collapse;
	font-size: 13px;
}

.central-processamentos__tabela th {
	text-align: left;
	padding: 8px 12px;
	border-bottom: 2px solid #e4e9da;
	color: #4a6320;
	font-weight: 700;
}

.central-processamentos__tabela td {
	padding: 8px 12px;
	border-bottom: 1px solid #eef1e8;
	vertical-align: middle;
}

.central-processamentos__status {
	display: inline-block;
	padding: 2px 10px;
	border-radius: 10px;
	font-size: 11px;
	font-weight: 700;
}

.central-processamentos__status--pendente {
	background-color: #f0f0f0;
	color: #666;
}

.central-processamentos__status--processando {
	background-color: #e5f0fb;
	color: #1c5f9e;
}

.central-processamentos__status--concluido {
	background-color: #ebf5da;
	color: #4a6320;
}

.central-processamentos__status--falhou {
	background-color: #fdeaea;
	color: #a33;
}

.central-processamentos__progresso {
	max-width: 340px;
}

.central-processamentos__etapa {
	display: block;
	font-size: 12px;
	color: #666;
	margin-bottom: 4px;
}

.central-processamentos__erro {
	display: inline-block;
	max-width: 320px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	color: #a33;
	font-size: 12px;
}

.central-processamentos__barra {
	height: 6px;
	border-radius: 3px;
	background-color: #e4e9da;
	overflow: hidden;
}

.central-processamentos__barra-preenchida {
	height: 100%;
	border-radius: 3px;
	background-color: #95bb49;
	transition: width 0.6s ease;
}

/* ==========================================================================
   Modal de detalhes (log) do job de vínculo
   ========================================================================== */
.central-processamentos__modal-overlay {
	position: fixed;
	inset: 0;
	z-index: 20;
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: rgba(0, 0, 0, 0.35);
}

.central-processamentos__modal {
	width: min(560px, calc(100vw - 32px));
	max-height: 80vh;
	overflow: auto;
	padding: 16px;
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.central-processamentos__modal-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
}

.central-processamentos__modal-lista {
	display: grid;
	grid-template-columns: max-content 1fr;
	gap: 6px 16px;
	margin: 0;
	font-size: 13px;
}

.central-processamentos__modal-lista dt {
	color: #4a6320;
	font-weight: 700;
}

.central-processamentos__modal-lista dd {
	margin: 0;
	word-break: break-word;
}

/* Erro completo (multilinha), sem o ellipsis da grade */
.central-processamentos__modal-erro {
	padding: 8px 12px;
	border-radius: 6px;
	background-color: #fdeaea;
	color: #a33;
	font-size: 12px;
	white-space: pre-wrap;
	word-break: break-word;
}
</style>
